import { useEffect, useState } from 'react';

import { useApi, ApiError, type MeResponse } from './client';

/**
 * Fetch the authenticated caller's identity + effective role from /api/v1/me.
 *
 * Returns one of:
 *   { kind: 'loading' }
 *   { kind: 'ok'; data: MeResponse }
 *   { kind: 'error'; message: string; status?: number }
 *
 * Used by:
 *   • Nav components to show/hide the Admin link based on role.
 *   • Profile-style components that want to render a "viewing as <role>"
 *     indicator.
 *   • Route components that gate themselves to a minimum role.
 *
 * Phase 1 simplification: one /me call per component that mounts the hook.
 * Phase 1.5 should hoist this into a single context provider above the
 * router so all consumers share one fetch.
 */
export type CurrentUserState =
  | { kind: 'loading' }
  | { kind: 'ok'; data: MeResponse }
  | { kind: 'error'; message: string; status?: number };

export function useCurrentUser(): CurrentUserState {
  const callApi = useApi();
  const [state, setState] = useState<CurrentUserState>({ kind: 'loading' });

  useEffect(() => {
    let active = true;
    callApi<MeResponse>('/me')
      .then((data) => {
        if (active) setState({ kind: 'ok', data });
      })
      .catch((err: unknown) => {
        if (!active) return;
        if (err instanceof ApiError) {
          setState({
            kind: 'error',
            status: err.status,
            message: err.message,
          });
        } else {
          const message = err instanceof Error ? err.message : 'Unknown error';
          setState({ kind: 'error', message });
        }
      });
    return () => {
      active = false;
    };
    // callApi is a fresh closure each render; deliberately excluded.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return state;
}
